import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle2, XCircle, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `https://admin.krazybets.online/api`;

const NEW_LOGO_URL = "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/pvmmvvvw__krazy%20bets%20logo%20Final%20png1.png";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | error | resend
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  const token = params.get('token');

  const verify = useCallback(async () => {
    if (!token) {
      setStatus('resend');
      setMessage('No verification token provided.');
      return;
    }
    try {
      const res = await axios.post(`${API}/auth/verify-email`, { token });
      setStatus('success');
      setMessage(res.data.message || 'Email verified!');
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (err) {
      setStatus('error');
      const d = err.response?.data?.detail;
      setMessage(typeof d === 'string' ? d : 'Verification failed. The link may be expired.');
    }
  }, [token, navigate]);

  useEffect(() => { verify(); }, [verify]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResending(true);
    try {
      const res = await axios.post(`${API}/auth/resend-verification`, { email: resendEmail });
      toast.success(res.data.message || 'Verification email sent');
      setMessage('A fresh verification link has been sent to your inbox.');
      setStatus('resend_sent');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not send verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A]">
      <Header showAuth={false} />
      <div className="flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md" data-testid="verify-email-card">
          <div className="card-dark text-center">
            <img src={NEW_LOGO_URL} alt="KrazyBets" className="h-20 w-20 mx-auto mb-4" />

            {status === 'verifying' && (
              <>
                <Loader2 className="h-12 w-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
                <h1 className="text-2xl font-black uppercase text-white">Verifying your email...</h1>
                <p className="text-gray-400 mt-2">Just a moment.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle2 className="h-16 w-16 text-[#22C55E] mx-auto mb-4" />
                <h1 className="text-2xl font-black uppercase text-white">Email Verified!</h1>
                <p className="text-gray-300 mt-3">{message}</p>
                <p className="text-gray-500 text-sm mt-4">Redirecting to your dashboard…</p>
                <Link to="/dashboard"
                      className="btn-primary inline-block mt-5 px-6 py-2 rounded-full"
                      data-testid="verify-success-go-dashboard">
                  Go to Dashboard
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-2xl font-black uppercase text-white">Verification failed</h1>
                <p className="text-gray-300 mt-3">{message}</p>
                <div className="mt-5">
                  <p className="text-gray-400 text-sm mb-3">
                    Need a new link? Enter your email and we'll resend it.
                  </p>
                  <form onSubmit={handleResend} className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      required
                      className="input-dark"
                      data-testid="resend-email-input"
                    />
                    <Button type="submit" disabled={resending} className="btn-primary" data-testid="resend-email-btn">
                      {resending ? 'Sending…' : 'Resend'}
                    </Button>
                  </form>
                </div>
              </>
            )}

            {status === 'resend' && (
              <>
                <Mail className="h-16 w-16 text-[#D4AF37] mx-auto mb-4" />
                <h1 className="text-2xl font-black uppercase text-white">Resend verification</h1>
                <p className="text-gray-300 mt-2">
                  Enter the email you signed up with and we'll send you a new verification link.
                </p>
                <form onSubmit={handleResend} className="mt-5 flex gap-2">
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    required
                    className="input-dark"
                    data-testid="resend-email-input"
                  />
                  <Button type="submit" disabled={resending} className="btn-primary" data-testid="resend-email-btn">
                    {resending ? 'Sending…' : 'Resend'}
                  </Button>
                </form>
              </>
            )}

            {status === 'resend_sent' && (
              <>
                <CheckCircle2 className="h-16 w-16 text-[#22C55E] mx-auto mb-4" />
                <h1 className="text-2xl font-black uppercase text-white">Check your inbox</h1>
                <p className="text-gray-300 mt-3">{message}</p>
                <Link to="/login"
                      className="btn-primary inline-block mt-5 px-6 py-2 rounded-full"
                      data-testid="resend-back-to-login">
                  Back to Login
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
